import { useQuery } from '@tanstack/react-query';
import { MembersService } from '@/services/domain/MembersService';
import { Photo } from '@/models/Photo';

/**
 * Default number of public photos requested for a member's profile preview row
 * and the profile-photo carousel. Both surfaces use this so they share the same
 * React Query cache entry — one fetch, one identical array of photos.
 */
export const PROFILE_PHOTOS_PREVIEW_MAX_COUNT = 10;

export const memberPreviewPhotosQueryKey = (
  memberId: number | undefined,
  maxCount: number,
  withDetails: boolean
) => ['photos', 'member-preview', memberId, maxCount, withDetails] as const;

/**
 * Builds the member's photo carousel with the profile photo first, matching the
 * on-screen flow of the profile photo above the preview row.
 *
 * The profile photo is not always part of the loaded public photos — for some
 * members it lives outside their public set (e.g. it's an older photo past the
 * preview page, or a dedicated avatar). When it isn't found we synthesize a
 * photo from the passed id/url so the avatar is still swipeable as the first
 * item. When no profile photo id/url is provided the photos are returned as-is.
 */
export const buildProfilePhotoCarousel = (
  photos: Photo[] | undefined,
  profilePhotoId: number | null | undefined,
  profilePhotoUrl: string | null | undefined
): Photo[] => {
  const list = photos ?? [];
  if (!profilePhotoId || !profilePhotoUrl) return list;
  const rest = list.filter((p) => p.Id !== profilePhotoId && !p.IsProfilePhoto);
  const existing = list.find((p) => p.IsProfilePhoto || p.Id === profilePhotoId);
  let head = existing;
  if (!head) {
    head = new Photo();
    head.Id = profilePhotoId;
    head.FullSizeUrl = profilePhotoUrl;
    const memberRef = list[0]?.Member;
    if (memberRef) head.Member = memberRef;
    // The member's avatar icon is the small thumbnail of this same profile photo;
    // prefer it so the viewer's blurred background loads a tiny image instead of
    // the full-size. Fall back to the full-size URL only when it's unavailable.
    head.IconUrl = memberRef?.IconUrl || profilePhotoUrl;
  }
  return [head, ...rest];
};

interface UseMemberPreviewPhotosOptions {
  maxCount?: number;
  withDetails?: boolean;
  enabled?: boolean;
}

/** Fetches a member's public photos for the profile preview row and carousel. */
export const useMemberPreviewPhotos = (
  memberId: number | undefined,
  {
    maxCount = PROFILE_PHOTOS_PREVIEW_MAX_COUNT,
    withDetails = true,
    enabled = true,
  }: UseMemberPreviewPhotosOptions = {}
) => {
  return useQuery({
    queryKey: memberPreviewPhotosQueryKey(memberId, maxCount, withDetails),
    queryFn: async () => {
      const response = await MembersService.getMemberPublicPhotos(memberId!, {
        take: maxCount,
        withDetails,
      });
      return response.PhotosItems;
    },
    enabled: enabled && !!memberId,
  });
};
